import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Feature } from '../../core/client/features.gen'
import { useFeature } from '../../core/features/FeaturesProvider'
import { modelsFunctions } from '../../api/modules/modelsApi'
import { AIModel, ModelStatus, UserModelSettings } from '../../types/modelTypes'

const MODELS_QUERY_KEY = ['models']
const MODEL_SETTINGS_QUERY_KEY = ['model-settings']
const MODEL_STATUS_QUERY_KEY = ['model-status']

export function useModels() {
  const queryClient = useQueryClient()
  const modelSelectionEnabled = useFeature(Feature.MODEL_SELECTION)

  const { data: models = [], isLoading: isLoadingModels } = useQuery<AIModel[]>({
    queryKey: MODELS_QUERY_KEY,
    queryFn: () => modelsFunctions.fetchModels('completion'),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: modelSelectionEnabled,
  })

  // Health changes far faster than the catalog. No polling: a model tripping
  // mid-conversation surfaces through the fallback toast.
  const { data: statusResponse } = useQuery({
    queryKey: MODEL_STATUS_QUERY_KEY,
    queryFn: () => modelsFunctions.fetchModelStatus(),
    staleTime: 30 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    enabled: modelSelectionEnabled,
  })

  const { data: settingsResponse, isLoading: isLoadingSettings } = useQuery({
    queryKey: MODEL_SETTINGS_QUERY_KEY,
    queryFn: () => modelsFunctions.fetchUserModelSettings(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: modelSelectionEnabled,
  })

  const userSettings: Record<string, UserModelSettings> = {}
  if (settingsResponse?.items) {
    for (const item of settingsResponse.items) {
      userSettings[item.model_name] = item
    }
  }

  const updateSettingsMutation = useMutation({
    mutationFn: ({ modelName, settings }: { modelName: string; settings: Record<string, number | string> }) =>
      modelsFunctions.updateUserModelSettings(modelName, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_SETTINGS_QUERY_KEY })
    },
  })

  const modelStatus: Record<string, ModelStatus> = {}
  for (const entry of statusResponse?.available_models ?? []) {
    modelStatus[entry.name] = entry
  }

  return {
    models,
    modelStatus,
    userSettings,
    isLoading: modelSelectionEnabled && (isLoadingModels || isLoadingSettings),
    updateSettings: updateSettingsMutation.mutate,
    isUpdating: updateSettingsMutation.isPending,
    isUpdateError: updateSettingsMutation.isError,
  }
}
